import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import Avatar from "@/components/Avatar";
import { toast } from "sonner";
import { X, Users, UserPlus, UserCheck } from "lucide-react";

export default function FollowStats() {
  const [data, setData] = useState({ followers: [], following: [], following_ids: [] });
  const [modal, setModal] = useState(null); // "followers" | "following"

  const load = async () => {
    try { const { data: d } = await api.get("/members/follows/mine"); setData(d); } catch { /* ignore */ }
  };
  useEffect(() => { load(); }, []);

  const toggleFollow = async (u) => {
    const isFollowing = data.following_ids.includes(u.id);
    try {
      if (isFollowing) await api.delete(`/members/${u.id}/follow`);
      else { await api.post(`/members/${u.id}/follow`); toast.success(`Following ${u.name}`); }
      await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const list = modal === "followers" ? data.followers : data.following;

  return (
    <>
      <div className="flex items-center gap-3 mb-6 animate-fade-up" data-testid="follow-stats">
        <Users className="w-4 h-4 text-pb-cyan" />
        {[["followers", data.followers.length, "Followers"], ["following", data.following.length, "Following"]].map(([k, n, label]) => (
          <button key={k} data-testid={`follow-stats-${k}`} onClick={() => setModal(k)}
            className="flex items-baseline gap-1.5 border border-pb-border bg-pb-surface px-4 py-2 hover:border-pb-cyan transition-colors">
            <span className="font-display font-black text-lg text-white">{n}</span>
            <span className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">{label}</span>
          </button>
        ))}
      </div>

      {modal && (
        <div className="fixed inset-0 z-[85] bg-black/75 flex items-center justify-center p-4" onClick={() => setModal(null)}>
          <div data-testid="follow-list-modal" className="w-full max-w-sm bg-pb-surface border border-pb-border animate-fade-up" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-2 p-4 border-b border-pb-border">
              <Users className="w-4 h-4 text-pb-cyan" />
              <span className="font-display font-black uppercase flex-1 capitalize">{modal}</span>
              <button data-testid="follow-modal-close" onClick={() => setModal(null)} className="text-zinc-500 hover:text-white"><X className="w-5 h-5" /></button>
            </div>
            <div className="max-h-72 overflow-y-auto divide-y divide-pb-border/50">
              {list.length === 0 && (
                <div className="p-6 text-sm text-zinc-600 text-center">
                  {modal === "followers" ? "No followers yet — get active in the Community!" : "You're not following anyone yet — tap Follow on a player card."}
                </div>
              )}
              {list.map((u) => {
                const isFollowing = data.following_ids.includes(u.id);
                return (
                  <div key={u.id} data-testid={`follow-row-${u.id}`} className="flex items-center gap-3 px-4 py-3">
                    <Avatar src={u.avatar} name={u.name} size={36} />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-semibold truncate">{u.name}</div>
                      {u.location && <div className="text-[10px] text-zinc-500 truncate">{u.location}</div>}
                    </div>
                    <button data-testid={`follow-toggle-${u.id}`} onClick={() => toggleFollow(u)}
                      className={`flex items-center gap-1.5 text-[10px] font-black uppercase tracking-widest px-3 py-1.5 transition-all active:scale-95 ${
                        isFollowing ? "border border-pb-border text-zinc-300 hover:border-red-500 hover:text-red-400" : "bg-pb-red text-black hover:bg-[#BEF264]"}`}>
                      {isFollowing ? <><UserCheck className="w-3 h-3" /> Following</> : <><UserPlus className="w-3 h-3" /> Follow</>}
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
